import { FORM_FILE_MAX_BYTES } from './form-file.js';
import type { Money } from './money.js';

export const INSTITUTION_VAULT_KINDS = ['cash', 'bank', 'other'] as const;
export type InstitutionVaultKind = (typeof INSTITUTION_VAULT_KINDS)[number];

/** The kinds an administrator defines; the cash vault is every institution's own, made with it. */
export const DEFINABLE_VAULT_KINDS = ['bank', 'other'] as const;

export const INSTITUTION_TRANSFER_STATUSES = [
  'requested',
  'approved',
  'completed',
  'cancelled',
  'reversed',
] as const;
export type InstitutionTransferStatus = (typeof INSTITUTION_TRANSFER_STATUSES)[number];

/**
 * The statuses that hold the organization's reconciled money for a transfer:
 * asked for, approved, or done. A cancelled or reversed transfer holds nothing.
 */
export const COMMITTED_TRANSFER_STATUSES = ['requested', 'approved', 'completed'] as const;

export type TransferRoom = {
  readonly collected: Money;
  readonly completed: Money;
  readonly pending: Money;
};

/**
 * What an organization may still ask to move to one institution, from one
 * account, in one currency: the money its confirmed matches collected there,
 * less every transfer already done and every one still waiting. Never negative.
 */
export function transferableMinor(room: TransferRoom): bigint {
  const left = room.collected.amountMinor - room.completed.amountMinor - room.pending.amountMinor;
  return left > 0n ? left : 0n;
}

/** Which of the two limits a transfer request meets first. */
export type TransferLimit = 'room' | 'account';

export type TransferCapacity = {
  /** Collected at the institution less transfers done and waiting; negative when over-committed. */
  readonly roomMinor: bigint;
  /** The account's balance less what requested and approved transfers off it will take. */
  readonly accountMinor: bigint;
  /** The most one request may ask for: the lesser of the two, never negative. */
  readonly transferableMinor: bigint;
  /** The limit that binds; the room on a tie, because it is the one the organization can explain. */
  readonly limitedBy: TransferLimit;
};

export type TransferFigures = {
  readonly collectedMinor: bigint;
  readonly completedMinor: bigint;
  readonly pendingMinor: bigint;
  readonly balanceMinor: bigint;
  readonly waitingMinor: bigint;
};

/**
 * What one request from one account, to one institution, in one currency may
 * ask for — docs/decisions/0129 §3. The write refuses by `transferRefusalFor`
 * and the read offers `transferableMinor`, both from this one function, so the
 * figure on the screen is the figure the server accepts.
 */
export function transferCapacity(figures: TransferFigures): TransferCapacity {
  const roomMinor = figures.collectedMinor - figures.completedMinor - figures.pendingMinor;
  const accountMinor = figures.balanceMinor - figures.waitingMinor;
  const limitedBy: TransferLimit = accountMinor < roomMinor ? 'account' : 'room';
  const lesser = limitedBy === 'account' ? accountMinor : roomMinor;
  return {
    roomMinor,
    accountMinor,
    transferableMinor: lesser > 0n ? lesser : 0n,
    limitedBy,
  };
}

/**
 * The limit a request for `amountMinor` breaks, or null when it is accepted.
 * The room is asked first: it is the organization's own money at this
 * institution, the account balance the cash behind it.
 */
export function transferRefusalFor(
  capacity: TransferCapacity,
  amountMinor: bigint,
): TransferLimit | null {
  if (amountMinor > capacity.roomMinor) return 'room';
  if (amountMinor > capacity.accountMinor) return 'account';
  return null;
}

/**
 * Whether the reconciled money still covers the transfers committed against
 * it. A match whose reversal would leave less than that is refused.
 */
export function coversCommitted(collectedMinor: bigint, committedMinor: bigint): boolean {
  return collectedMinor >= committedMinor;
}

/** The longest free-text description an outflow carries. */
export const VAULT_OUTFLOW_DESCRIPTION_MAX_LENGTH = 500;

/** An outflow's document is a photograph or a PDF, read under the same cap a form's file is. */
export const VAULT_OUTFLOW_DOCUMENT_MAX_BYTES = FORM_FILE_MAX_BYTES;
